import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormLabel,
    Modal,
    ModalDialog,
    Sheet,
    Stack,
    Table,
    Textarea,
    Typography
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';

const tableSheetSx = {
    width: '100%',
    maxWidth: '100%',
    borderRadius: 'sm',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: 360,
    WebkitOverflowScrolling: 'touch',
    border: '1px solid',
    borderColor: 'divider'
};

const tableSx = {
    width: 'max-content',
    minWidth: '100%',
    tableLayout: 'fixed',
    '& tbody tr:nth-of-type(even)': { backgroundColor: '#f9f9f9' },
    '& tbody tr:hover': { backgroundColor: '#fff5ed' },
    '& thead': { position: 'sticky', top: 0, zIndex: 10 },
    '& thead th': {
        backgroundColor: '#eb7a24',
        color: 'white',
        fontWeight: 'bold',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        borderBottom: '2px solid #e55125',
        whiteSpace: 'nowrap'
    },
    '& td, & th': {
        padding: '12px',
        border: '1px solid',
        borderColor: 'divider',
        verticalAlign: 'middle',
        overflow: 'hidden'
    },
    '& th:nth-of-type(1), & td:nth-of-type(1)': { width: 80, minWidth: 80, whiteSpace: 'nowrap' },
    '& th:nth-of-type(2), & td:nth-of-type(2)': { width: 130, minWidth: 130, whiteSpace: 'nowrap' },
    '& th:nth-of-type(3), & td:nth-of-type(3)': { width: 240, minWidth: 220, whiteSpace: 'normal', wordBreak: 'break-word' },
    '& th:nth-of-type(4), & td:nth-of-type(4)': { width: 260, minWidth: 260, whiteSpace: 'normal' }
};

/**
 * Dashboard preview of "Confirm for Campus Online" rows.
 * Renders only when there are 2+ pending rows.
 */
const CampusOnlineConfirmPreview = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [rows, setRows] = useState([]);
    const [confirming, setConfirming] = useState(false);
    const [notInterestedLoading, setNotInterestedLoading] = useState(false);
    const [reasonModalOpen, setReasonModalOpen] = useState(false);
    const [selectedCampusId, setSelectedCampusId] = useState(null);
    const [reason, setReason] = useState('');

    const shouldRender = useMemo(() => !loading && rows.length > 0, [loading, rows.length]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            setSuccess('');
            const res = await axiosInstance.get('/students/student-campuses', {
                params: {
                    onlinePage: 1,
                    onlineLimit: 50,
                    confirmedPage: 1,
                    confirmedLimit: 1
                }
            });
            const onlineCampuses = Array.isArray(res.data?.onlineCampuses) ? res.data.onlineCampuses : [];
            setRows(onlineCampuses);
        } catch (e) {
            console.error('Campus online preview load error:', e);
            setError(e?.response?.data?.errorMessage || e?.message || 'Failed to load campus confirmations.');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const confirmCampus = async (campusId) => {
        if (!campusId) return;
        try {
            setConfirming(true);
            setError('');
            setSuccess('');
            const res = await axiosInstance.post('/students/confirm-campus', { campusId });
            if (!res.data?.success) {
                throw new Error(res.data?.errorMessage || 'Failed to confirm campus.');
            }
            setSuccess('Campus confirmed.');
            await load();
        } catch (e) {
            console.error('Campus confirm error:', e);
            setError(e?.response?.data?.errorMessage || e?.message || 'Failed to confirm campus.');
            await load();
        } finally {
            setConfirming(false);
        }
    };

    const openNotInterested = (campusId) => {
        setSelectedCampusId(campusId || null);
        setReason('');
        setReasonModalOpen(true);
    };

    const submitNotInterested = async () => {
        if (!selectedCampusId) return;
        try {
            setNotInterestedLoading(true);
            setError('');
            setSuccess('');
            const res = await axiosInstance.post('/students/not-interested-campus', {
                campusId: selectedCampusId,
                reason: reason || null
            });
            if (!res.data?.success) {
                throw new Error(res.data?.errorMessage || 'Failed to submit response.');
            }
            setSuccess('Response submitted.');
            setReasonModalOpen(false);
            setSelectedCampusId(null);
            setReason('');
            await load();
        } catch (e) {
            console.error('Campus not interested error:', e);
            setError(e?.response?.data?.errorMessage || e?.message || 'Failed to submit response.');
        } finally {
            setNotInterestedLoading(false);
        }
    };

    const closeModal = () => {
        if (notInterestedLoading) return;
        setReasonModalOpen(false);
        setSelectedCampusId(null);
        setReason('');
    };

    if (!shouldRender) {
        return null;
    }

    return (
        <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3, minWidth: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                    <Box sx={{ minWidth: 220 }}>
                        <Typography level="title-lg" sx={{ color: '#eb7a24' }}>
                            Confirm for Campus Online
                        </Typography>
                        <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.25 }}>
                            You have been shortlisted based on the preferences given by you.
                        </Typography>
                    </Box>
                </Box>

                {error && (
                    <Alert color="danger" variant="soft" sx={{ mb: 1.5 }}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert color="success" variant="soft" sx={{ mb: 1.5 }}>
                        {success}
                    </Alert>
                )}

                <Sheet variant="outlined" sx={tableSheetSx}>
                    <Table aria-label="online campus confirmation preview table" stickyHeader sx={tableSx}>
                        <thead>
                            <tr>
                                <th>Sl. No.</th>
                                <th>Campus Code</th>
                                <th>Company Name</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((campus) => (
                                <tr key={campus.serialNumber || campus.campusId}>
                                    <td>{campus.serialNumber}</td>
                                    <td>{campus.campusCode}</td>
                                    <td>{campus.companyName || '-'}</td>
                                    <td>
                                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                            <Button
                                                size="sm"
                                                variant="solid"
                                                onClick={() => confirmCampus(campus.campusId)}
                                                disabled={confirming || notInterestedLoading}
                                                sx={{ backgroundColor: '#eb7a24', '&:hover': { backgroundColor: '#e55125' } }}
                                            >
                                                {confirming ? 'Confirming...' : 'Confirm'}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outlined"
                                                color="neutral"
                                                onClick={() => openNotInterested(campus.campusId)}
                                                disabled={confirming || notInterestedLoading}
                                                sx={{
                                                    borderColor: '#6e7072',
                                                    color: '#6e7072',
                                                    '&:hover': { borderColor: '#404041', backgroundColor: '#f5f5f5' }
                                                }}
                                            >
                                                Not Interested
                                            </Button>
                                        </Stack>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Sheet>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size="sm" />
                    </Box>
                )}

                <Modal open={reasonModalOpen} onClose={closeModal}>
                    <ModalDialog aria-labelledby="not-interested-modal" sx={{ maxWidth: 520 }}>
                        <Typography id="not-interested-modal" component="h2" level="h4" sx={{ mb: 0.5 }}>
                            Not Interested - Please Share Reason
                        </Typography>
                        <Typography textColor="text.secondary" sx={{ mb: 2 }}>
                            This information helps us analyze and improve our system.
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <FormLabel>Reason (Optional)</FormLabel>
                            <Textarea
                                placeholder="Please share why you are not interested in this campus..."
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                minRows={3}
                                sx={{ mt: 1 }}
                            />
                        </Box>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button variant="outlined" onClick={closeModal} disabled={notInterestedLoading}>
                                Cancel
                            </Button>
                            <Button
                                variant="solid"
                                onClick={submitNotInterested}
                                loading={notInterestedLoading}
                                sx={{ backgroundColor: '#6e7072', '&:hover': { backgroundColor: '#404041' } }}
                            >
                                {notInterestedLoading ? 'Submitting...' : 'Submit'}
                            </Button>
                        </Stack>
                    </ModalDialog>
                </Modal>
            </CardContent>
        </Card>
    );
};

export default CampusOnlineConfirmPreview;
